import { useContext } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import { AnimatePresence } from '@tamagui/animate-presence';

import { Box, BoxProps } from '../../Box';
import { Form } from '../../Form';
import { InputLabelContext } from '../../shared/InputLabelContext';

const { formLayout: formLayoutTokens } = tokens;

export type FormContentProps = BoxProps & {
  /**
   * A unique string or number to use as the key for the animation
   */
  animationKey: string | number;
  /**
   * If true, the enter animation will be skipped
   */
  skipEnterAnimation?: boolean;
  /**
   * If true, the exit animation will be skipped
   */
  skipExitAnimation?: boolean;
};

/**
 * Container for form page title and questions. You must pass a unique
 * `animationKey` prop to trigger the enter and exit animations (for example,
 * an id for each page in a multi-page flow).
 */
export const FormContent = ({
  children,
  animationKey,
  skipEnterAnimation,
  skipExitAnimation,
  ...props
}: FormContentProps) => {
  const { top, leftRight, mobileBetweenContainers, gap } = formLayoutTokens.margin.contentContainer;

  // Have to render InputLabelContext again below the animation container
  // so the animated input still has access to it while it exits and doesn't
  // throw an error
  const { inputId } = useContext(InputLabelContext);

  return (
    <Form.Form flexGrow={1}>
      <AnimatePresence exitBeforeEnter>
        <Box
          key={animationKey}
          animation="decelerateFast"
          animateOnly={['opacity', 'transform']}
          enterStyle={skipEnterAnimation ? undefined : { y: 40, opacity: 0 }}
          exitStyle={skipExitAnimation ? undefined : { y: 0, opacity: 0 }}
          flexGrow={1}
          paddingTop={top.xsmallviewport}
          paddingHorizontal={leftRight.xsmallviewport}
          gap={mobileBetweenContainers}
          $group-formLayout-largerThanXS={{
            paddingTop: top.smallviewport,
            paddingHorizontal: leftRight.smallviewport,
          }}
          $group-formLayout-largerThanSM={{
            paddingTop: top.mediumviewport,
            paddingHorizontal: leftRight.mediumviewport,
            flexDirection: 'row',
            gap: gap.mediumviewport,
          }}
          $group-formLayout-largerThanMD={{
            paddingTop: top.largeviewport,
            paddingHorizontal: leftRight.largeviewport,
            gap: gap.largeviewport,
          }}
          $group-formLayout-largerThanLG={{
            gap: gap.xlargeviewport,
          }}
          {...props}
        >
          <InputLabelContext.Provider value={{ inputId }}>{children}</InputLabelContext.Provider>
        </Box>
      </AnimatePresence>
    </Form.Form>
  );
};
